// @flow
import React, { useCallback, useMemo, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { toastr } from 'react-redux-toastr';
import {
  Button,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import useHasPermission from '../../hooks/useHasPermission';
import useIsAffordableProperty from '../../hooks/useIsAffordableProperty';
import usePropertyHasProgram from '../../hooks/usePropertyHasProgram';
import useApplicableFractionInitializationPreflight from '../../hooks/data-fetching/useApplicableFractionInitializationPreflight'; // eslint-disable-line max-len
import useInitializeApplicableFractionsMutation from '../../hooks/data-fetching/useInitializeApplicableFractionsMutation'; // eslint-disable-line max-len
import useApplicableFractionInitializationState from '../../hooks/data-fetching/useApplicableFractionInitializationState'; // eslint-disable-line max-len
import { AFFORDABLE_PROGRAMS } from '../../utils/affordable';
import ConfirmationModal from './ConfirmationModal';
import messages from './messages';
import {
  INITIALIZATION_FAILURE,
  allBuildingsHaveApplicableFraction,
  classifyInitializationFailure,
} from './utils';
import type { NaurFinding } from '../../services/naurService';

type Props = {
  selectedProperty: Object,
  buildings: ?Array<Object>,
  intl: Object,
};

/**
 * The one-time applicable fraction initialization action, shared by Manage Buildings and
 * Unit Compliance.
 *
 * Availability is decided here, not by the backend: the product rule is that every
 * building must have at least one fraction set, which is also what "the
 * property is subject to NAUR" means. The preflight GET fires when the dialog opens,
 * never on page load, and is the only source of household-level blockers.
 */
const InitializeApplicableFractionsAction = ({
  selectedProperty,
  buildings,
  intl,
}: Props) => {
  const { naur } = useFlags();
  const canEditFloorplan = useHasPermission('floor-plan-update');
  const isAffordableProperty = useIsAffordableProperty(selectedProperty);
  const isLIHTCProperty = usePropertyHasProgram(
    selectedProperty,
    AFFORDABLE_PROGRAMS.LIHTC,
  );

  const [isOpen, setIsOpen] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [blockedFindings, setBlockedFindings] =
    useState<?Array<NaurFinding>>(null);
  const [failureMessage, setFailureMessage] = useState<?string>(null);

  const organizationId = selectedProperty?.organizationId;
  const propertyId = selectedProperty?.id;

  const preflight = useApplicableFractionInitializationPreflight({
    organizationId,
    propertyId,
    enabled: isOpen,
  });

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setBlockedFindings(null);
    setFailureMessage(null);
  }, []);

  const finish = useCallback(() => {
    setIsDone(true);
    closeDialog();
  }, [closeDialog]);

  const mutation = useInitializeApplicableFractionsMutation({
    organizationId,
    propertyId,
    options: {
      onSuccess: () => {
        toastr.success(
          intl.formatMessage(messages.successTitle),
          intl.formatMessage(messages.successBody),
        );
        finish();
      },
      onError: (error) => {
        const outcome = classifyInitializationFailure(error);

        if (outcome.kind === INITIALIZATION_FAILURE.BLOCKED) {
          const returned = outcome.preflight?.findings;

          if (Array.isArray(returned) && returned.length > 0) {
            setBlockedFindings(returned);
            setFailureMessage(null);
          } else {
            // A blocked result with nothing renderable must still say why the run was refused.
            setFailureMessage(
              outcome.preflight?.message ??
                intl.formatMessage(messages.genericFailure),
            );
          }
          return;
        }

        setFailureMessage(
          outcome.message ?? intl.formatMessage(messages.genericFailure),
        );
      },
    },
  });

  const initializationState = useApplicableFractionInitializationState({
    organizationId,
    propertyId,
  });

  const fractionsConfigured = useMemo(
    () => allBuildingsHaveApplicableFraction(buildings),
    [buildings],
  );

  // isDone hides the button instantly when this user's own run succeeds, with no
  // refetch latency. An already-initialized property (someone else's run, or this
  // property's own error refresh) is instead covered by the initializationState
  // query, which the mutation invalidates on every error. Neither replaces the other.
  const isVisible =
    Boolean(naur) &&
    canEditFloorplan &&
    isAffordableProperty &&
    isLIHTCProperty &&
    !isDone &&
    !initializationState.data?.alreadyInitialized;

  if (!isVisible) {
    return null;
  }

  // The 422 body replaces the preflight's findings once it arrives — it is the same
  // payload, revalidated against committed state at the moment of the POST.
  const findings = blockedFindings ?? preflight.data?.findings;

  return (
    <>
      {/* The <span> is load-bearing: a disabled MUI button swallows pointer events,
          so the tooltip needs a wrapper that still receives them. */}
      <Tooltip
        title={intl.formatMessage(
          fractionsConfigured
            ? messages.actionTooltip
            : messages.fractionsNotConfiguredTooltip,
        )}
      >
        <span>
          <Button
            variant="primary"
            disabled={!fractionsConfigured}
            onClick={() => setIsOpen(true)}
          >
            {intl.formatMessage(messages.actionLabel)}
          </Button>
        </span>
      </Tooltip>

      <ConfirmationModal
        open={isOpen}
        intl={intl}
        findings={findings}
        alreadyInitialized={Boolean(preflight.data?.alreadyInitialized)}
        isPreflightLoading={preflight.isFetching}
        hasPreflightError={Boolean(preflight.isError)}
        isSubmitting={mutation.isLoading}
        failureMessage={failureMessage}
        onClose={closeDialog}
        onConfirm={() => {
          setFailureMessage(null);
          mutation.mutate();
        }}
      />
    </>
  );
};

export default InitializeApplicableFractionsAction;
